<template>
  <div class="flex flex-col p-3">

    <AppModal v-model="showTransactionDetails" title="Transaction Details" hide-footer size="lg">
      <div v-if="selectedTransaction" class="space-y-4">
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div class="space-y-2">
            <p><span class="text-gray-500">PRN:</span> <span class="font-medium">{{ selectedTransaction.prn }}</span>
            </p>
            <p><span class="text-gray-500">Tracking Number:</span> <span
              class="font-medium">{{ selectedTransaction.trackingNumber }}</span></p>
            <p><span class="text-gray-500">Invoice Number:</span> <span
              class="font-medium">{{ selectedTransaction.invoiceNo }}</span></p>
            <p><span class="text-gray-500">Transaction Ref:</span> <span
              class="font-medium">{{ selectedTransaction.transactionRef }}</span></p>
            <p><span class="text-gray-500">Service:</span> <span
              class="font-medium">{{ cleanEntityType(selectedTransaction.transactionType) }}</span></p>
            <p><span class="text-gray-500">Date:</span> <span
              class="font-medium">{{ dateFormat(selectedTransaction.createdDate) }}</span></p>
          </div>
          <div class="space-y-2">
            <p><span class="text-gray-500">Total Amount:</span> <span
              class="font-medium">UGX {{ selectedTransaction.grandTotal.toLocaleString() }}</span></p>
            <p><span class="text-gray-500">Sub Total:</span> <span
              class="font-medium">UGX {{ selectedTransaction.subTotal.toLocaleString() }}</span></p>
            <p><span class="text-gray-500">Total Tax:</span> <span
              class="font-medium">UGX {{ selectedTransaction.totalTax.toLocaleString() }}</span></p>
            <p>
              <span class="text-gray-500">Payment Method:</span>
              <span class="font-medium"> {{ cleanUnderscoreString(selectedTransaction.paymentMethod) }}</span>
              <span v-if="selectedTransaction.paymentMethod === 'MOBILE_MONEY'"
                    class="text-primary text-xs ml-1">{{ selectedTransaction.msisdn }}</span>
            </p>
            <p class="flex items-center gap-2">
              <span class="text-gray-500">Transaction Status:</span>
              <span :class="statusPill(selectedTransaction.status)">{{
                  selectedTransaction.status.toLowerCase()
                }}</span>
            </p>
            <p class="flex items-center gap-2">
              <span class="text-gray-500">PRN Status:</span>
              <span :class="statusPill(selectedTransaction.prnStatus)">{{
                  selectedTransaction.prnStatus.toLowerCase()
                }}</span>
            </p>
          </div>
        </div>
        <div class="flex justify-end pt-2">
          <button class="btn-primary" @click="retryVerification(selectedTransaction, -1)">
            <i class="fa fa-redo mr-1"></i> Retry Transaction
          </button>
        </div>
      </div>
    </AppModal>

    <AppModal v-model="showBulkSync" title="Bulk move to queue" hide-footer size="md">
      <form class="space-y-4" @submit.prevent="submitBulkSync">
        <p class="text-sm text-gray-600">
          Queue transactions for the selected service type within the date range.
        </p>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Start date</label>
          <input
            v-model="bulkForm.startDate"
            type="date"
            required
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            :disabled="bulkSubmitting"
          />
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">End date</label>
          <input
            v-model="bulkForm.endDate"
            type="date"
            required
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            :disabled="bulkSubmitting"
          />
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Transaction type</label>
          <select
            v-model="bulkForm.transactionType"
            required
            class="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            :disabled="bulkSubmitting"
          >
            <option value="" disabled>-- Select transaction type --</option>
            <option
              v-for="opt in TRANSACTION_TYPE_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >{{ opt.text }}</option>
          </select>
        </div>
        <div class="flex justify-end gap-2 pt-1">
          <button
            type="button"
            class="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
            :disabled="bulkSubmitting"
            @click="showBulkSync = false"
          >
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="bulkSubmitting || !canSubmitBulk">
            <i v-if="bulkSubmitting" class="fa fa-spinner fa-spin mr-1"></i>
            <i v-else class="fa fa-list-check mr-1"></i>
            {{ bulkSubmitting ? "Queuing..." : "Move to queue" }}
          </button>
        </div>
      </form>
    </AppModal>

    <!-- Filter bar -->
    <div class="flex flex-wrap items-center gap-3 py-3">
      <input
        type="text"
        v-model="trackingNumber"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
        placeholder="Search by Tracking No"
        @keyup.enter="fetch"
      />
      <input
        type="text"
        v-model="prn"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-40"
        placeholder="Search by PRN"
        @keyup.enter="fetch"
      />
      <select v-model="status"
              class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              @change="fetch">
        <option :value="null">-- Filter by Status --</option>
        <option value="PENDING">Pending</option>
        <option value="COMPLETED">Completed</option>
        <option value="CANCELLED">Cancelled</option>
        <option value="FAILED">Failed</option>
      </select>
      <select v-model="prnStatus"
              class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              @change="fetch">
        <option :value="null">-- Filter by PRN Status --</option>
        <option value="PENDING">Pending</option>
        <option value="CREDITED">Credited</option>
        <option value="FAILED">Failed</option>
      </select>
      <select v-model="paymentMethod"
              class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              @change="fetch">
        <option :value="null">-- Filter by Payment --</option>
        <option value="MOBILE_MONEY">Mobile Money</option>
        <option value="ALREADY_PAID">Already Paid</option>
        <option value="COUNTER">Counter</option>
      </select>
      <button type="button" class="btn-primary ml-auto" @click="openBulkSync">
        <i class="fa fa-list-check mr-1"></i> Bulk move to queue
      </button>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>PRN</th>
          <th>Tracking No.</th>
          <th>Service</th>
          <th>Payment Method</th>
          <th>Total</th>
          <th>PRN Status</th>
          <th>Status</th>
          <th>Date</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td>
              <div class="h-3 bg-gray-200 rounded w-6"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-28"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-24"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-28"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-20"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-16"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-16"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-16"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-20"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-20"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(transaction, idx) in transactions" :key="idx">
            <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td class="font-bold">
              <div class="flex items-center gap-1">
                <span>{{ transaction.prn }}</span>
                <button v-if="canReleasePrn" class="btn-sm-danger text-xs" @click="releasePrnPrompt(transaction, idx)">
                  <i class="fa fa-times mr-1"></i> release
                  </button>
                </div>
              </td>
            <td class="font-bold font-mono text-primary">{{ transaction.trackingNumber }}</td>
            <td>{{ cleanEntityType(transaction.transactionType) }}</td>
            <td>{{ cleanUnderscoreString(transaction.paymentMethod.toLowerCase()) }}</td>
            <td class="font-medium">UGX {{ transaction.grandTotal.toLocaleString() }}</td>
            <td>
                <span v-if="transaction.prnStatus" :class="statusPill(transaction.prnStatus)">
                  {{ transaction.prnStatus.toLowerCase() }}
                </span>
              </td>
            <td>
                <span v-if="transaction.status" :class="statusPill(transaction.status)">
                  {{ transaction.status.toLowerCase() }}
                </span>
              </td>
            <td>{{ dateFormat(transaction.createdDate) }}</td>
            <td>
              <div class="flex gap-1">
                <button class="btn-sm-primary" @click="view(transaction)">
                  <i class="fa fa-eye mr-1"></i> View
                </button>
                <button v-if="transaction.prnStatus !== 'CREDITED'" class="btn-sm-primary"
                        @click="retryVerification(transaction, idx)">
                  <i class="fa fa-redo mr-1"></i> Retry
                </button>
              </div>
              </td>
            </tr>
        </template>
        <tr v-if="!loading && transactions.length === 0">
          <td colspan="10" class="text-center py-6 text-gray-400 text-sm">No results found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-4 px-1">
      <div class="flex items-center gap-3">
        <button
          class="btn-sm-primary"
          :disabled="currentPage <= 1"
          :class="currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
          @click="previous"
        >
          <i class="fa fa-chevron-left mr-1"></i> Previous
        </button>
        <span class="text-xs text-gray-500">Page {{ currentPage }} of {{ pagination.totalPages }}</span>
        <button
          class="btn-sm-primary"
          :disabled="currentPage >= pagination.totalPages"
          :class="currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
          @click="next"
        >
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
      <div class="flex items-center gap-2">
        <select v-model="limit"
                class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                @change="fetch">
          <option :value="12">12</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="text-xs text-gray-500">
          {{ currentPage === 1 ? 1 : (currentPage - 1) * limit + 1 }} – {{
            currentPage * limit
          }} of {{ pagination.totalElements }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useAbility } from "@casl/vue";
import AppModal from "@/components/ui/AppModal.vue";
import { cleanEntityType, cleanUnderscoreString, dateFormat } from "@/commons/composables/utilities.ts";
import { useToast } from "@/composables";
import { useBillingStore } from "@/domains/home/services/billing/stores";
import { TRANSACTION_TYPE_OPTIONS } from "@/domains/home/services/billing/types";

const billingStore = useBillingStore();
const { can } = useAbility();
const { successToast, errorToast, warningToast } = useToast();

const showTransactionDetails = ref(false);
const showBulkSync = ref(false);
const bulkSubmitting = ref(false);
const loading = ref(false);
const prn = ref<string | null>(null);
const trackingNumber = ref<string | null>(null);
const paymentMethod = ref<string | null>(null);
const prnStatus = ref<string | null>(null);
const status = ref<string | null>(null);
const currentPage = ref(1);
const limit = ref(12);
const transactions = ref<any[]>([]);
const pagination = ref({ totalElements: 0, totalPages: 0 });
const selectedTransaction = ref<any>(null);
const bulkForm = ref({
  startDate: "",
  endDate: "",
  transactionType: "",
});

const canReleasePrn = computed(() => can("can_release_prn", "general"));

const canSubmitBulk = computed(() => {
  const { startDate, endDate, transactionType } = bulkForm.value;
  return Boolean(startDate && endDate && transactionType);
});

const statusPill = (status: string) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "CREDITED" || status === "COMPLETED") return base + "bg-green-50 text-green-700";
  if (status === "FAILED") return base + "bg-red-50 text-red-700";
  if (status === "PENDING") return base + "bg-gray-100 text-gray-600";
  if (status === "CANCELED" || status === "CANCELLED") return base + "bg-orange-50 text-orange-600";
  return base + "bg-gray-100 text-gray-600";
};

onMounted(() => fetch());

const fetch = () => {
  loading.value = true;
  billingStore.fetchTransactions({
    prn: prn.value, prnStatus: prnStatus.value, status: status.value,
    paymentMethod: paymentMethod.value, trackingNumber: trackingNumber.value,
    page: currentPage.value, pageSize: limit.value
  });
};

const view = (data: any) => {
  selectedTransaction.value = data;
  showTransactionDetails.value = true;
};

const openBulkSync = () => {
  bulkForm.value = { startDate: "", endDate: "", transactionType: "" };
  showBulkSync.value = true;
};

const submitBulkSync = async () => {
  const { startDate, endDate, transactionType } = bulkForm.value;
  if (!startDate || !endDate) {
    warningToast("Validation", "Start date and end date are required");
    return;
  }
  if (!transactionType) {
    warningToast("Validation", "Select a transaction type");
    return;
  }
  if (startDate > endDate) {
    warningToast("Validation", "Start date must be on or before end date");
    return;
  }

  bulkSubmitting.value = true;
  try {
    const res = await billingStore.bulkSynchronizeTransactions({
      startDate,
      endDate,
      transactionType,
    });
    const message =
      (res?.data as { message?: string } | undefined)?.message ||
      "Transactions queued for synchronization";
    successToast("Success", message);
    showBulkSync.value = false;
    fetch();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast(
      "Error",
      err.response?.data?.message || err.message || "Could not queue transactions"
    );
  } finally {
    bulkSubmitting.value = false;
  }
};

const retryVerification = (data: any, idx: number) => {
  const user = JSON.parse(sessionStorage.getItem("current_user") || "{}");
  billingStore.retryTransactionVerification({ userId: user.userId, prn: data.prn });
};

const releasePrnPrompt = (data: any, idx: number) => {
  const user = JSON.parse(sessionStorage.getItem("current_user") || "{}");
  billingStore.releasePRNTransaction({ userId: user.userId, prn: data.prn });
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

watch(() => billingStore.transactions, (data) => {
  loading.value = false;
  if (data?.content) {
    transactions.value = data.content;
    pagination.value = { totalPages: data.totalPages, totalElements: data.totalElements };
  } else if (Array.isArray(data)) {
    transactions.value = data;
  }
})

watch(() => billingStore.transactionSuccess, (data) => {
  if (data) {
    loading.value = false;
    fetch();
  }
});

watch(() => billingStore.transactionError, (data) => {
  if (data) loading.value = false;
});

watch(() => billingStore.prnReleaseSuccess, (data) => {
  if (data) fetch();
});

watch(() => billingStore.prnReleaseError, (data) => {
  if (data) loading.value = false;
});
</script>
